import { markdownToPlainText } from '@littlebird/markdown-utils';

import { Icons } from '@/assets/icons/icons';
import { HtmlEditor, MarkdownValueEditor } from '@/components/ui/editor';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Markdown } from '@/components/ui/smoothdown';
import TextArea from '@/components/ui/TextArea';
import { cn } from '@/lib/utils';
import { htmlToPlainText, sanitizeHtml } from '@/utils/html';

import { useRevealedFields } from '../use-revealed-fields';

import { ActionCardAttachments } from './ActionCardAttachments';
import { DateTimeRangeEditor } from './DateTimeRangeEditor';
import { ExternalLink } from './ExternalLink';
import { FieldOptionMenu } from './FieldOptionMenu';
import { SanitizedHtmlValue } from './FieldValueRenderers';

import type {
  ActionCardAttachment,
  ActionCardField,
  ActionCardPreviousFieldValues,
  EditableActionCardField,
} from '@littlebird/action-cards';

type ActionCardFieldsProps = {
  fields: readonly ActionCardField[];
  attachments?: readonly ActionCardAttachment[];
  editing?: boolean;
  compact?: boolean;
  previousFieldValues?: ActionCardPreviousFieldValues;
  onFieldChange?: (fieldKey: string, value: string) => void;
  onFieldActivate?: (fieldKey: string) => void;
  autoFocusFieldKey?: string;
};

const FIELD_BOX_CLASS =
  '-mx-1.5 min-w-0 flex-1 rounded-md border border-transparent px-1.5 py-0.5 text-sm font-medium leading-relaxed text-foreground';

const FIELD_BOX_EDIT_CLASS =
  'bg-surface-hover focus:bg-surface-active outline-none';

const MULTILINE_WELL_CLASS =
  'block max-h-80 w-full overflow-x-hidden overflow-y-auto rounded-lg border border-transparent bg-surface-hover p-2.5 text-sm leading-relaxed text-foreground';

const ACTIVATABLE_VALUE_CLASS =
  'cursor-text text-left outline-none transition-colors hover:bg-surface-active/70 focus-visible:ring-2 focus-visible:ring-accent';

const FIELD_LABEL_CLASS = 'w-24 shrink-0 text-xs text-foreground-muted sm:w-28';

const MULTILINE_LABEL_CLASS = cn(FIELD_LABEL_CLASS, 'pt-[calc(0.625rem+1px)]');

type MultilineFieldEditorProps = {
  field: EditableActionCardField;
  autoFocus: boolean;
  onFieldChange: (fieldKey: string, value: string) => void;
};

// The editor opens sized to its content: single-line values get a compact box
// that grows as the user types; only content that is already multiline opens
// tall. ~80 chars approximates one wrapped line for paragraph-style values.
const estimateRows = (value: string): number => {
  const plain =
    value.includes('<') && value.includes('>')
      ? htmlToPlainText(sanitizeHtml(value))
      : value;
  const lines = plain
    .split('\n')
    .reduce(
      (count, line) => count + Math.max(1, Math.ceil(line.length / 80)),
      0
    );
  return Math.min(Math.max(lines, 1), 10);
};

// The trailing empty paragraph is a line the draft view does not render, so it
// would make the field grow the moment editing starts.
const RICH_EDITOR_EXCLUDED_EXTENSIONS = ['trailingNode'];

const MultilineFieldEditor = ({
  field,
  autoFocus,
  onFieldChange,
}: MultilineFieldEditorProps) => {
  if (field.editor === 'html') {
    return (
      <span className={cn(MULTILINE_WELL_CLASS, 'border-border-strong')}>
        <HtmlEditor
          id={field.key}
          value={sanitizeHtml(field.value)}
          onChange={(html) => onFieldChange(field.key, html)}
          placeholder={field.placeholder ?? field.label}
          ariaLabel={field.label}
          featureSet="email"
          proseSize="sm"
          autoFocus={autoFocus}
          excludeExtensions={RICH_EDITOR_EXCLUDED_EXTENSIONS}
          className="p-0"
        />
      </span>
    );
  }

  if (field.editor === 'markdown') {
    return (
      <span className={cn(MULTILINE_WELL_CLASS, 'border-border-strong')}>
        <MarkdownValueEditor
          id={field.key}
          value={field.value}
          onChange={(value) => onFieldChange(field.key, value)}
          placeholder={field.placeholder ?? field.label}
          ariaLabel={field.label}
          featureSet="email"
          proseSize="sm"
          autoFocus={autoFocus}
          excludeExtensions={RICH_EDITOR_EXCLUDED_EXTENSIONS}
          className="p-0"
        />
      </span>
    );
  }

  return (
    <TextArea
      value={field.value}
      onChange={(event) => onFieldChange(field.key, event.target.value)}
      rows={estimateRows(field.value)}
      placeholder={field.placeholder}
      aria-label={field.label}
      autoFocus={autoFocus}
      className={cn(
        MULTILINE_WELL_CLASS,
        'min-h-0 resize-none border-border-strong shadow-none [field-sizing:content]'
      )}
    />
  );
};

const MultilineFieldValue = ({
  field,
  onActivate,
}: {
  field: ActionCardField;
  onActivate?: () => void;
}) => {
  if (!field.collapsible) {
    const value =
      field.format === 'html' ? (
        <SanitizedHtmlValue html={field.value} />
      ) : field.format === 'markdown' ? (
        <Markdown
          id={field.key}
          content={field.value}
          className="prose prose-bird prose-sm max-w-none text-left"
        />
      ) : (
        field.value
      );
    if (onActivate) {
      return (
        <button
          type="button"
          aria-label={`Edit ${field.label}`}
          onClick={onActivate}
          className={cn(
            MULTILINE_WELL_CLASS,
            field.format !== 'html' &&
              field.format !== 'markdown' &&
              'whitespace-pre-wrap',
            ACTIVATABLE_VALUE_CLASS
          )}
        >
          {value}
        </button>
      );
    }
    return (
      <span
        className={cn(
          MULTILINE_WELL_CLASS,
          field.format !== 'html' &&
            field.format !== 'markdown' &&
            'whitespace-pre-wrap'
        )}
      >
        {value}
      </span>
    );
  }

  const collapsedText = (
    field.format === 'html'
      ? htmlToPlainText(sanitizeHtml(field.value))
      : field.format === 'markdown'
        ? markdownToPlainText(field.value)
        : field.value
  ).replace(/\s*\n+\s*/g, ' · ');

  return (
    <HoverCard openDelay={250} closeDelay={120}>
      <HoverCardTrigger asChild>
        <span
          tabIndex={0}
          role={onActivate ? 'button' : undefined}
          aria-label={onActivate ? `Edit ${field.label}` : undefined}
          onClick={onActivate}
          className={cn(
            'block cursor-default truncate rounded-lg border border-transparent bg-surface-hover p-2.5 text-sm leading-relaxed text-foreground outline-none',
            onActivate && ACTIVATABLE_VALUE_CLASS
          )}
        >
          {collapsedText}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="max-h-80 w-96 max-w-[min(24rem,90vw)] overflow-y-auto"
      >
        {field.format === 'html' ? (
          <SanitizedHtmlValue html={field.value} />
        ) : field.format === 'markdown' ? (
          <div className="prose prose-bird max-w-none select-text">
            <Markdown
              id={field.key}
              content={field.value}
              className="prose-sm"
            />
          </div>
        ) : (
          <span className="block whitespace-pre-wrap text-sm leading-relaxed">
            {field.value}
          </span>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

export const ActionCardFields = ({
  fields,
  attachments = [],
  editing = false,
  compact = false,
  previousFieldValues,
  onFieldChange,
  onFieldActivate,
  autoFocusFieldKey,
}: ActionCardFieldsProps) => {
  const { visibleFields, hiddenEditableFields, revealField, revealedFocusKey } =
    useRevealedFields(fields, editing);
  const focusFieldKey = revealedFocusKey ?? autoFocusFieldKey;

  return (
    <span
      className={cn(
        'flex flex-col gap-2 px-4 pb-4 pt-1',
        compact && 'gap-2.5 px-0 pb-0 pt-0'
      )}
    >
      {visibleFields.map((field) => {
        const editable = editing && field.editable && onFieldChange;
        const activate =
          !editing && field.editable && onFieldActivate
            ? () => onFieldActivate(field.key)
            : undefined;
        const selectOptions =
          field.editable && field.editor === 'select'
            ? (field.options ?? [])
            : [];
        const chooseOption =
          selectOptions.length > 0 &&
          onFieldChange &&
          (editing || onFieldActivate)
            ? (value: string) => {
                if (!editing) onFieldActivate?.(field.key);
                onFieldChange(field.key, value);
              }
            : undefined;
        const previousValue = previousFieldValues?.[field.key];
        const autoFocus = focusFieldKey === field.key;

        if (field.kind === 'multiline') {
          return (
            <span key={field.key} className="flex items-start gap-3.5">
              <span className={MULTILINE_LABEL_CLASS}>{field.label}</span>
              <span className="flex min-w-0 flex-1 flex-col">
                {editable ? (
                  <MultilineFieldEditor
                    field={field}
                    autoFocus={autoFocus}
                    onFieldChange={onFieldChange}
                  />
                ) : (
                  <MultilineFieldValue field={field} onActivate={activate} />
                )}
              </span>
            </span>
          );
        }

        if (
          editing &&
          onFieldChange &&
          field.editable &&
          field.editor === 'datetime-range' &&
          field.range
        ) {
          return (
            <span key={field.key} className="flex items-baseline gap-3.5">
              <span className={FIELD_LABEL_CLASS}>{field.label}</span>
              <span className={cn(FIELD_BOX_CLASS, 'flex')}>
                <DateTimeRangeEditor
                  range={field.range}
                  onChange={(value) => onFieldChange(field.key, value)}
                />
              </span>
            </span>
          );
        }

        return (
          <span key={field.key} className="flex items-baseline gap-3.5">
            <span className={FIELD_LABEL_CLASS}>{field.label}</span>
            {chooseOption ? (
              <FieldOptionMenu
                label={field.label}
                options={selectOptions}
                onSelect={chooseOption}
                className={cn(
                  FIELD_BOX_CLASS,
                  'inline-flex w-auto items-center gap-1'
                )}
              >
                <span>{field.value}</span>
                <Icons.chevronDown
                  className="size-3 shrink-0 text-foreground-muted"
                  aria-hidden="true"
                />
              </FieldOptionMenu>
            ) : editable ? (
              <input
                value={field.value}
                onChange={(event) =>
                  onFieldChange(field.key, event.target.value)
                }
                aria-label={field.label}
                autoComplete="off"
                autoFocus={autoFocus}
                className={cn(FIELD_BOX_CLASS, FIELD_BOX_EDIT_CLASS)}
              />
            ) : !field.editable && field.href ? (
              <ExternalLink
                href={field.href}
                className="min-w-0 flex-1 break-words text-sm font-medium text-foreground"
              >
                {field.value}
              </ExternalLink>
            ) : activate ? (
              <button
                type="button"
                aria-label={`Edit ${field.label}`}
                onClick={activate}
                className={cn(FIELD_BOX_CLASS, ACTIVATABLE_VALUE_CLASS)}
              >
                {field.value}
              </button>
            ) : (
              <span
                className={cn(
                  FIELD_BOX_CLASS,
                  'flex flex-wrap items-baseline gap-2'
                )}
              >
                {previousValue ? (
                  <>
                    <span className="sr-only">Changed from </span>
                    <span className="font-normal text-foreground-subtle line-through">
                      {previousValue}
                    </span>
                    <Icons.navigateRight
                      className="size-3 shrink-0 self-center text-foreground-muted"
                      aria-hidden="true"
                    />
                    <span className="sr-only"> to </span>
                  </>
                ) : null}
                <span>{field.value}</span>
              </span>
            )}
          </span>
        );
      })}
      {hiddenEditableFields.length > 0 ? (
        <span className="flex items-baseline gap-3.5">
          <span className={FIELD_LABEL_CLASS} />
          <span className="-mx-1.5 flex min-w-0 flex-1 flex-wrap items-baseline gap-2 px-1.5">
            {hiddenEditableFields.map((field) => (
              <button
                key={field.key}
                type="button"
                onClick={() => revealField(field.key)}
                className="whitespace-nowrap text-xs text-foreground-muted hover:text-foreground"
              >
                + {field.label}
              </button>
            ))}
          </span>
        </span>
      ) : null}
      <ActionCardAttachments attachments={attachments} />
    </span>
  );
};
